import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SearchInput from "./SearchInput/SearchInput";
import AdCard from "./AdCard/AdCard";
import InterestCard from "./InterestCard/InterestCard";
import "./adPage.css";
import NoResults from "./NoAdResults/NoAdResults";
import MapSection from "./MapSection/MapSection";
import { pluralizeRoomType } from "../../utils/RoomtypeHelper";
import SEOTags from "../../utils/SEOTags";
import Pagination from "./Pagination/Pagination";
import useDebouncedNavigate from "./utils/hooks/DebounceNavigate";
import { buildQueryParams } from "./utils/QueryUtils";
import useFilters from "./utils/hooks/useFilters";
import { fetchFilteredAds } from "./api/AdPageAPI";
import { buildFilterParams } from "./utils/BuildFilterParams";
import { validateURLParameters } from "./utils/urlValidation";
import RoundedButton from "../../components/Buttons/RoundedButton/RoundedButton";
import DisplayMunicipalityText from "./MunicipalityText/DisplayMunicipalityText";
import DynamicText, { useDynamicText } from "./DynamicText/DynamicText";
import Spinner from "../../components/Spinner/Spinner";
import municipalitiesData from "../../assets/municipalities.json";
import roomTypesData from "../../assets/roomTypes.json";
import { truncateForMeta } from "../../utils/truncateForMeta";
import { createSEOUrl } from "../../utils/createSEOUrl";
import RoundedLinkButton from "../../components/Buttons/RoundedLinkButton/RoundedLinkButton";
import Layout from "../../components/layout/Layout";

// Export URL builder function for use in other components
export const buildAdPageUrl = (
  selectedMunicipality = [],
  selectedRoomTypes = [],
) => {
  return selectedMunicipality.length === 0 && selectedRoomTypes.length === 0
    ? "/lediga-lokaler"
    : `/lediga-lokaler/${
        selectedMunicipality.length > 0
          ? selectedMunicipality.join("-")
          : "allakommuner"
      }${
        selectedRoomTypes.length > 0
          ? `/${selectedRoomTypes.map((rt) => rt.replace(/&/g, "%26")).join("-")}`
          : ""
      }`;
};

const AdPage = () => {
  const [showMap, setShowMap] = useState(true);
  const [showSmallMap, setShowSmallMap] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAds, setTotalAds] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [id, setId] = useState([]);
  const [allAds, setAllAds] = useState([]);
  const debouncedNavigate = useDebouncedNavigate();
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const {
    searchTerm,
    selectedMunicipality,
    selectedRoomTypes,
    selectedTime,
    selectedPriceRange,
    selectedAreaRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
    setSearchTerm,
    setSelectedMunicipality,
    setSelectedRoomTypes,
    setSelectedTime,
    setSelectedPriceRange,
    setSelectedAreaRange,
    setSelectedMoms,
    setSelectedContractType,
    setSelectedZones,
    resetFilters,
    areFiltersActive,
  } = useFilters(location);

  const searchInputRef = useRef(null);

  // Update clearFilters to remove explicit sessionStorage clearing
  const clearFilters = useCallback(() => {
    // 1. Clear visual input immediately
    searchInputRef.current?.clearInput?.();

    // 2. Reset filters + search term state (sessionStorage clearing happens automatically in useFilters)
    resetFilters();

    // 3. Reset page
    setCurrentPage(1);
  }, [resetFilters]);

  const adsPerPage = 39;

  const renderedAds = id.flatMap((item, index) => [
    <li key={item._id}>
      <AdCard id={item} />
    </li>,
    (index + 1) % 20 === 0 && index + 1 !== id.length ? (
      <li key={`interestCard${index}`}>
        <InterestCard />
      </li>
    ) : null,
  ]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setShowMap(windowWidth < 800 ? false : true);
  }, [windowWidth]);

  useEffect(() => {
    setShowSmallMap(windowWidth < 800 ? true : false);
  }, [windowWidth]);

  const filterParams = useMemo(
    () => ({
      type_of_room: selectedRoomTypes.join(","),
      municipality: selectedMunicipality.join(","),
      priceTypes: selectedTime.join(","),
      ...(selectedPriceRange.min !== null && {
        min_rent: selectedPriceRange.min,
      }),
      ...(selectedPriceRange.max !== null && {
        max_rent: selectedPriceRange.max,
      }),
      ...(selectedAreaRange.min !== null && {
        min_area: selectedAreaRange.min,
      }),
      ...(selectedAreaRange.max !== null && {
        max_area: selectedAreaRange.max,
      }),
      vat_tax_required: selectedMoms.includes("true")
        ? "true"
        : selectedMoms.includes("false")
          ? "false"
          : undefined,
      contract_type: selectedContractType.join(",") || undefined,
      zone: selectedZones.join(",") || undefined,
    }),
    [
      selectedRoomTypes,
      selectedMunicipality,
      selectedTime,
      selectedPriceRange,
      selectedAreaRange,
      selectedMoms,
      selectedContractType,
      selectedZones,
    ],
  );

  const handleFetchFilteredAds = useCallback(() => {
    setIsLoading(true);
    // Don't reset totalAds here since we do it immediately above
    fetchFilteredAds({
      searchTerm,
      filterParams,
      currentPage,
      adsPerPage,
      setId,
      setTotalPages,
      setTotalAds,
      setAllAds,
      setIsLoading,
    });
  }, [filterParams, currentPage, searchTerm]);

  const updateURL = useCallback(
    (page = 1) => {
      const filterParams = {
        searchTerm: searchTerm || "",
        selectedMunicipality: selectedMunicipality || [],
        selectedRoomTypes: selectedRoomTypes || [],
        selectedTime: selectedTime || [],
        selectedPriceRange: selectedPriceRange || { min: 0, max: 50000 },
        selectedAreaRange: selectedAreaRange || { min: 0, max: 2000 },
        selectedMoms: selectedMoms || [],
        selectedContractType: selectedContractType || [],
        selectedZones: selectedZones || [],
      };
      const queryParamsString = buildQueryParams({ ...filterParams, page });

      const formatForURL = (values, encodeAmpersand = false) =>
        values
          .map((v) => {
            const lower = v.toLowerCase();
            return encodeAmpersand ? lower.replace(/&/g, "%26") : lower;
          })
          .join("-");

      const municipalityPath =
        selectedMunicipality.length > 0
          ? `${formatForURL(selectedMunicipality)}`
          : "allakommuner";

      const roomTypesPath =
        selectedRoomTypes.length > 0
          ? `/${formatForURL(selectedRoomTypes, true)}`
          : "";

      const path =
        selectedMunicipality.length === 0 && selectedRoomTypes.length === 0
          ? "/lediga-lokaler"
          : `/lediga-lokaler/${municipalityPath}${roomTypesPath}`;

      // Building the final URL
      const url = `${path}${queryParamsString ? `?${queryParamsString}` : ""}`;

      // Navigate to the updated URL
      debouncedNavigate(url);
    },
    [
      debouncedNavigate,
      searchTerm,
      selectedAreaRange,
      selectedMoms,
      selectedMunicipality,
      selectedPriceRange,
      selectedRoomTypes,
      selectedTime,
      selectedContractType,
      selectedZones,
    ],
  );

  const handlePageChange = useCallback(
    (page) => {
      setCurrentPage(page);
      const queryParams = new URLSearchParams(location.search);
      queryParams.set("p", page);
      navigate({ search: queryParams.toString() });
      fetchFilteredAds({
        filterParams,
        currentPage: page,
        adsPerPage,
        setId,
        setTotalPages,
        setTotalAds,
        searchTerm,
        setAllAds,
        setIsLoading,
      });
      window.scrollTo({ top: 0, behavior: "instant" });
    },
    [navigate, location.search, filterParams, searchTerm],
  );

  const prevFiltersRef = useRef({
    selectedMunicipality,
    selectedRoomTypes,
    selectedTime,
    selectedPriceRange,
    selectedAreaRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
  });

  useEffect(() => {
    const prevFilters = prevFiltersRef.current;
    if (
      JSON.stringify(prevFilters) !==
      JSON.stringify({
        selectedMunicipality,
        selectedRoomTypes,
        selectedTime,
        selectedPriceRange,
        selectedAreaRange,
        selectedMoms,
        selectedContractType,
        selectedZones,
      })
    ) {
      setCurrentPage(1);
      updateURL(1);
    }

    prevFiltersRef.current = {
      selectedMunicipality,
      selectedRoomTypes,
      selectedTime,
      selectedPriceRange,
      selectedAreaRange,
      selectedMoms,
      selectedContractType,
      selectedZones,
    };
  }, [
    selectedMunicipality,
    selectedRoomTypes,
    selectedTime,
    selectedPriceRange,
    selectedAreaRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
    updateURL,
  ]);

  useEffect(() => {
    handleFetchFilteredAds(buildFilterParams);
  }, [
    handleFetchFilteredAds,
    selectedRoomTypes,
    selectedMunicipality,
    selectedTime,
    selectedPriceRange,
    selectedAreaRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
    searchTerm,
    filterParams,
  ]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const page = parseInt(queryParams.get("p")) || 1;
    setCurrentPage(page);
    fetchFilteredAds({
      filterParams,
      currentPage: page,
      adsPerPage,
      setId,
      setTotalPages,
      setTotalAds,
      searchTerm,
      setAllAds,
      setIsLoading,
    });
  }, [location.search, filterParams, searchTerm]);

  // Watch for filters + searchTerm being reset
  useEffect(() => {
    const noFiltersActive =
      selectedMunicipality.length === 0 &&
      selectedRoomTypes.length === 0 &&
      selectedTime.length === 0 &&
      selectedPriceRange.min === null &&
      selectedPriceRange.max === null &&
      selectedAreaRange.min === null &&
      selectedAreaRange.max === null &&
      selectedMoms.length === 0 &&
      selectedContractType.length === 0 &&
      selectedZones.length === 0 &&
      searchTerm === "";

    if (noFiltersActive) {
      updateURL(1); // Safe to run here: state is now flushed
    }
  }, [
    selectedMunicipality,
    selectedRoomTypes,
    selectedTime,
    selectedPriceRange,
    selectedAreaRange,
    selectedMoms,
    selectedContractType,
    selectedZones,
    searchTerm,
    updateURL,
  ]);

  // isValidMunicipality
  const isValidMunicipality = useCallback((municipality) => {
    return municipalitiesData.municipalities.some(
      (validMunicipality) =>
        validMunicipality.toLowerCase() === municipality.toLowerCase(),
    );
  }, []);

  const isValidRoomType = useCallback((roomType) => {
    // remove / with & and decode the roomType
    const decodedRoomType = decodeURIComponent(roomType).replace(/&/g, "/");

    return roomTypesData.roomTypes.some(
      (validRoomType) =>
        validRoomType.toLowerCase() === decodedRoomType.toLowerCase(),
    );
  }, []);

  // Title logic
  const getTitle = useCallback(() => {
    const formatRoomType = (roomType) =>
      roomType === "kontorshotell&co-working"
        ? "kontorshotell & co-working"
        : pluralizeRoomType(roomType);

    const roomTypePluarized =
      selectedRoomTypes.length > 1
        ? "lokaler"
        : selectedRoomTypes.length === 1
          ? formatRoomType(selectedRoomTypes[0])
          : "lokaler";

    const municipalityText =
      selectedMunicipality.length === 0
        ? "i Sverige"
        : selectedMunicipality.length === 1 &&
            isValidMunicipality(selectedMunicipality[0])
          ? `i ${
              selectedMunicipality[0].charAt(0).toUpperCase() +
              selectedMunicipality[0].slice(1)
            }`
          : "";

    return `(${totalAds}) Lediga ${roomTypePluarized} att hyra ${municipalityText}`;
  }, [selectedMunicipality, selectedRoomTypes, totalAds, isValidMunicipality]);

  const title = useMemo(() => getTitle(), [getTitle]);

  const dynamicText = useDynamicText(selectedMunicipality, selectedRoomTypes);

  const metaDescription = useMemo(
    () => truncateForMeta(dynamicText, 160, "sv"),
    [dynamicText],
  );

  const url = buildAdPageUrl(selectedMunicipality, selectedRoomTypes);

  const BreadcrumbListSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        item: { "@id": "https://www.vakansa.se/", name: "Hem" },
      },
      {
        "@type": "ListItem",
        position: 2,
        item: { "@id": `https://www.vakansa.se${url}`, name: "Lediga lokaler" },
      },
    ],
  };

  const ItemListSchema = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: id.map((ad, index) => {
        const urlPath = `/lediga-lokaler/${createSEOUrl(
          ad.municipality,
        )}/${createSEOUrl(ad.type_of_room)}/${createSEOUrl(ad.address)}/${
          ad._id
        }`;

        return {
          "@type": "ListItem",
          position: (currentPage - 1) * adsPerPage + index + 1,
          url: `https://www.vakansa.se${urlPath}`,
        };
      }),
    }),
    [id, currentPage],
  );

  const combinedJsonLd = [BreadcrumbListSchema, ItemListSchema];

  // Effect for Municipality validation
  useEffect(() => {
    if (
      selectedMunicipality.length === 1 &&
      !isValidMunicipality(selectedMunicipality[0])
    ) {
      // Redirect to 404 page
      navigate("/404");
    }
  }, [selectedMunicipality, isValidMunicipality, navigate]);

  // Effect for roomType validation
  useEffect(() => {
    if (
      selectedRoomTypes.length === 1 &&
      !isValidRoomType(selectedRoomTypes[0])
    ) {
      // Redirect to 404 page
      navigate("/404");
    }
  }, [selectedRoomTypes, isValidRoomType, navigate]);

  useEffect(() => {
    // Check if user was redirected from 404
    if (location.state?.from404ForInvalidParam) {
      clearFilters();
      return;
    }

    const params = new URLSearchParams(location.search);

    // Validate all URL parameters in one go
    validateURLParameters(params, navigate);
  }, [location, navigate, clearFilters]);

  return (
    <>
      <SEOTags
        title={title + "  "}
        description={metaDescription}
        canonicalUrl={url}
        ogImage={"/images/logo/meta-logo.jpg"}
        structuredData={combinedJsonLd}
      />
      <Layout>
        <main className="">
          <div className="ad-section-top-wrapper">
            <div className="ad-section-head-text">
              <h1>
                Lediga{" "}
                {selectedRoomTypes.length > 1
                  ? "lokaler"
                  : selectedRoomTypes.length === 1 &&
                      isValidRoomType(selectedRoomTypes[0])
                    ? pluralizeRoomType(selectedRoomTypes[0])
                    : "lokaler"}
                {selectedMunicipality.length > 1
                  ? ""
                  : selectedMunicipality.length === 1 &&
                      isValidMunicipality(selectedMunicipality[0])
                    ? ` i ${
                        selectedMunicipality[0].charAt(0).toUpperCase() +
                        selectedMunicipality[0].slice(1)
                      }`
                    : ""}
              </h1>
              <h2>
                Här kan du hyra{" "}
                {selectedRoomTypes.length > 1
                  ? "lokal"
                  : selectedRoomTypes.length === 1 &&
                      isValidRoomType(selectedRoomTypes[0])
                    ? selectedRoomTypes[0].replace(/&/g, " & ")
                    : "lokal"}
                {selectedMunicipality.length > 1
                  ? ""
                  : selectedMunicipality.length === 1 &&
                      isValidMunicipality(selectedMunicipality[0])
                    ? ` i ${selectedMunicipality[0]
                        .split(" ")
                        .map(
                          (part) =>
                            part.charAt(0).toUpperCase() +
                            part.slice(1).toLowerCase(),
                        )
                        .join(" ")}`
                    : ""}
              </h2>
              <div className="dynamic-text-wrapper">
                <DynamicText
                  selectedMunicipality={selectedMunicipality}
                  selectedRoomTypes={selectedRoomTypes}
                  allAds={allAds}
                />
                <div className="interest-section">
                  <div className="interest-section-text">
                    <h3>Hittar du inte rätt lokal?</h3>
                    <p>Vi hjälper dig hitta rätt – kostnadsfritt!</p>
                  </div>
                  <RoundedLinkButton
                    label={"Få lokalförslag"}
                    color={"green"}
                    size={"md"}
                    href={"/lokalforslag"}
                  />
                </div>
              </div>
            </div>
            <div className="ad-section-search">
              <SearchInput
                ref={searchInputRef}
                value={searchTerm}
                onChange={setSearchTerm}
                selectedMunicipality={selectedMunicipality}
                setSelectedMunicipality={setSelectedMunicipality}
                selectedRoomTypes={selectedRoomTypes}
                setSelectedRoomTypes={setSelectedRoomTypes}
                selectedTime={selectedTime}
                setSelectedTime={setSelectedTime}
                selectedPriceRange={selectedPriceRange}
                setSelectedPriceRange={setSelectedPriceRange}
                selectedAreaRange={selectedAreaRange}
                setSelectedAreaRange={setSelectedAreaRange}
                selectedMoms={selectedMoms}
                setSelectedMoms={setSelectedMoms}
                selectedContractType={selectedContractType}
                setSelectedContractType={setSelectedContractType}
                selectedZones={selectedZones}
                setSelectedZones={setSelectedZones}
              />
            </div>

            <div className="ad-section-top">
              <div className="results-counter">
                <p className="ad-page-results">{totalAds}&nbsp; resultat</p>
              </div>
              <div className="ad-page-button-wrapper">
                <label className="toggle">
                  <div className="ad-setion-toggle-container">
                    <p className="toggle-label">Visa karta</p>
                    <input
                      className="toggle-checkbox"
                      type="checkbox"
                      checked={showMap}
                      onChange={() => setShowMap((prevState) => !prevState)}
                    />
                    <div className="toggle-switch"></div>
                  </div>
                </label>
                <label className="toggle-small">
                  <div className="ad-setion-toggle-container">
                    <p className="toggle-label">Visa karta</p>
                    <input
                      className="toggle-checkbox"
                      type="checkbox"
                      checked={showSmallMap}
                      onChange={() =>
                        setShowSmallMap((prevState) => !prevState)
                      }
                    />
                    <div className="toggle-switch"></div>
                  </div>
                </label>
                <RoundedButton
                  label="Rensa"
                  color="green"
                  size="xs"
                  onClick={clearFilters}
                  disabled={!areFiltersActive()}
                />
              </div>
            </div>
          </div>

          <div
            className={`outer-wrapper-ad-section ${
              showMap ? "two-column" : "single-column"
            }`}
          >
            <div className="ad-section-container">
              {showSmallMap && (
                <section
                  className={`map-placeholder ${showSmallMap ? "map-visible" : ""}`}
                  aria-labelledby="map-heading"
                >
                  <h2 className="visually-hidden" id="map-heading">
                    Karta över annonser i sverige
                  </h2>
                  <div className="map-wrap-time">
                    <MapSection mapZoom={4} ad={allAds} />
                  </div>
                </section>
              )}

              <div>
                <ul className="ad-cards-container">
                  {isLoading ? (
                    <div className="spinner-wrapper">
                      <Spinner />
                    </div>
                  ) : (
                    <>
                      {renderedAds}
                      {id.length === 1 && <InterestCard />}
                      {id.length === 0 && <InterestCard />}
                    </>
                  )}
                </ul>

                {/* Visa NoResults om ≤5 annonser */}
                {!isLoading && id.length <= 5 && (
                  <NoResults
                    allFilters={filterParams}
                    totalAds={totalAds}
                    excludedIds={id.map((ad) => ad._id)}
                  />
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>

            {showMap && (
              <section
                className={`map-placeholder ${showMap ? "map-visible" : ""}`}
                aria-labelledby="map-heading"
              >
                <h2 className="visually-hidden" id="map-heading">
                  Karta över annonser i sverige
                </h2>
                <div className="map-wrap-time">
                  <MapSection mapZoom={5} ad={allAds} />
                </div>
              </section>
            )}
          </div>
        </main>
      </Layout>
      <section className="cities-section">
        <DisplayMunicipalityText selectedMunicipality={selectedMunicipality} />
      </section>
    </>
  );
};

export default AdPage;
